import { CalendarDay } from "~/shared.types";
import { icons } from "~/ui";
import { tw } from "~/utils";
import { getAdjacentMonth, getSelectedDayMeta } from "../utils";

type DayNavigatorProps = {
  days: CalendarDay[];
  selectedDay: CalendarDay;
  onDaySelect: (day: CalendarDay) => void;
  hasAppointments: (date: Date) => boolean;
  onMonthChange: (monthYear: { month: string; year: number }) => void;
  currentMonthYear: { month: string; year: number };
  maxAllowedDate: Date;
  firstAvailableDay: CalendarDay | null;
  onReturnToFirstAvailable: () => void;
};

export const DayNavigator = ({
  days,
  selectedDay,
  onDaySelect,
  hasAppointments,
  onMonthChange,
  currentMonthYear,
  maxAllowedDate,
  firstAvailableDay,
  onReturnToFirstAvailable,
}: DayNavigatorProps) => {
  const selectedIndex = days.findIndex(
    (d) => d.date.toDateString() === selectedDay.date.toDateString(),
  );

  const prevAvailableDay =
    selectedIndex > 0
      ? days
          .slice(0, selectedIndex)
          .reverse()
          .find((d) => hasAppointments(d.date))
      : undefined;

  const nextAvailableDay =
    selectedIndex !== -1
      ? days.slice(selectedIndex + 1).find((d) => hasAppointments(d.date))
      : undefined;

  const today = new Date();
  const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const prevMonthDate = getAdjacentMonth(currentMonthYear, -1);
  const nextMonthDate = getAdjacentMonth(currentMonthYear, 1);

  const lowerBoundMonthStart = firstAvailableDay
    ? new Date(
        firstAvailableDay.date.getFullYear(),
        firstAvailableDay.date.getMonth(),
        1,
      )
    : thisMonthStart;

  const canGoPrev = !!prevAvailableDay || prevMonthDate >= lowerBoundMonthStart;
  const canGoNext = !!nextAvailableDay || nextMonthDate <= maxAllowedDate;

  const handlePrev = () => {
    if (prevAvailableDay) {
      onDaySelect(prevAvailableDay);
    } else {
      onMonthChange({
        month: prevMonthDate.toLocaleString("en-US", { month: "long" }),
        year: prevMonthDate.getFullYear(),
      });
    }
  };

  const handleNext = () => {
    if (nextAvailableDay) {
      onDaySelect(nextAvailableDay);
    } else {
      onMonthChange({
        month: nextMonthDate.toLocaleString("en-US", { month: "long" }),
        year: nextMonthDate.getFullYear(),
      });
    }
  };

  const { label, distanceInDays } = getSelectedDayMeta(selectedDay);

  const showReturnButton =
    !!firstAvailableDay &&
    firstAvailableDay.date.toDateString() !== selectedDay.date.toDateString();

  return (
    <div className="flex flex-col gap-4 md:px-4 md:pt-2">
      <div className="flex items-center justify-between">
        <button
          onClick={handlePrev}
          disabled={!canGoPrev}
          aria-label="Previous available day"
          className="rounded-full p-1 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <icons.outline.ChevronLeftIcon className="text-indigo size-6" />
        </button>

        <div translate="no" className="notranslate text-center">
          <p className="text-midnight-indigo text-md font-medium md:text-lg">
            {label}
          </p>
          {distanceInDays && (
            <p className="text-md text-gray-500">({distanceInDays})</p>
          )}
        </div>

        <button
          onClick={handleNext}
          disabled={!canGoNext}
          aria-label="Next available day"
          className="rounded-full p-1 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <icons.outline.ChevronRightIcon className="text-violet size-6 font-medium" />
        </button>
      </div>

      <button
        onClick={onReturnToFirstAvailable}
        disabled={!showReturnButton}
        aria-hidden={!showReturnButton}
        className={tw(
          "text-violet flex items-center gap-1 text-sm font-medium underline disabled:pointer-events-none md:text-base",
          !showReturnButton && "invisible",
        )}
      >
        <icons.outline.ArrowLeftIcon className="stroke-3 size-4" />
        Return to first available
      </button>
    </div>
  );
};
